<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton
          preset="secondary"
          color="textPrimary"
          data-tour="account-menu"
          :size="props.big ? 'large' : undefined"
          :class="{ 'profile-anchor--big': props.big }"
        >
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <span class="profile-badge">
              <VaIcon name="mso-account_circle" />
              <span class="profile-label">Account</span>
            </span>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in displayOptions" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="onItemClick(item, $event)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../../../stores/auth'
import { useColors } from 'vuestic-ui'
import { requestReplay } from '../../../../composables/useOnboardingTour'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const props = withDefaults(
  defineProps<{
    options?: ProfileOptions[]
    big?: boolean
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'mso-settings',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            to: 'login',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)
const router = useRouter()
const authStore = useAuthStore()

// Admins and homeowners get an extra "Product tour" item that replays their onboarding tour.
const canReplayTour = computed(() => {
  const role = authStore.user?.role
  return (
    role === 'ASSOCIATION_ADMIN' || role === 'HOMEOWNER' || role === 'BOARD_AUDITOR' || role === 'GUARD'
  )
})
const displayOptions = computed(() => {
  const opts = props.options.map((g) => ({ ...g, list: [...g.list] }))
  if (canReplayTour.value) {
    const account = opts.find((g) => g.name === 'account')
    if (account) account.list.push({ name: 'tour', icon: 'mso-tour' })
  }
  return opts
})

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

const onItemClick = async (item: ProfileListItem, event?: Event) => {
  if (item.name === 'logout') {
    event?.preventDefault()
    authStore.logout()
    isShown.value = false
    await router.push({ name: 'login' })
  } else if (item.name === 'tour') {
    event?.preventDefault()
    isShown.value = false
    requestReplay(router, authStore.user?.role)
  } else {
    isShown.value = false
  }
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}

.profile-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: 600;
}

.profile-label {
  font-size: 0.9rem;
}

.profile-anchor--big {
  .profile-badge {
    font-size: 1.05rem;
  }
  .va-icon {
    font-size: 1.6rem;
  }
}
</style>
